import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ContentPageHeader, TableLink, TablePagination } from '@/components/global';
import { InventoryDataTableCard, InventoryListFiltersCard, InventoryTableHead } from '@/components/inventory';
import { MachineLink, RefillSourceBadge, RefillStatusBadge } from '@/components/machines';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiFetch } from '@/lib/api';
import {
  emptyRefillListMessage,
  formatRefillWhen,
  REFILL_LIST_FILTERS,
  refillSessionPath,
  type RefillListFilter,
  type RefillSessionListItem,
  type RefillSessionListResult,
} from '@/lib/refillSession';

const PAGE_SIZE = 25;

function isRefillListFilter(value: string | null): value is RefillListFilter {
  return REFILL_LIST_FILTERS.some((filter) => filter.value === value);
}

export function RefillsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const machineId = searchParams.get('machineId') ?? '';
  const filterParam = searchParams.get('filter');
  const filter: RefillListFilter = isRefillListFilter(filterParam)
    ? filterParam
    : machineId
      ? 'all'
      : 'needs_action';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const [items, setItems] = useState<RefillSessionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        filter,
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });
      if (machineId) params.set('machineId', machineId);

      const res = await apiFetch(`/api/v1/refill-sessions?${params}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.error === 'string' ? data.error : 'Failed to load refills');
      }
      const data: RefillSessionListResult = await res.json();
      setItems(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : 'Failed to load refills');
    } finally {
      setLoading(false);
    }
  }, [filter, page, machineId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateParams = (next: { filter?: RefillListFilter; page?: number }) => {
    const params = new URLSearchParams();
    const nextFilter = next.filter ?? filter;
    const nextPage = next.page ?? (next.filter ? 1 : page);
    if (nextFilter !== 'needs_action' || machineId) {
      params.set('filter', nextFilter);
    }
    if (machineId) params.set('machineId', machineId);
    if (nextPage > 1) params.set('page', String(nextPage));
    setSearchParams(params);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const machineName = items[0]?.machineName;
  const description = machineId
    ? `Refills for ${machineName || 'this machine'}, including sessions completed in Express.`
    : 'Track Cloud pick-and-dispatch and Express fills. Return leftover van stock to the hub from a completed session.';

  return (
    <div className="space-y-6">
      <ContentPageHeader
        title="Refills"
        description={description}
        back={machineId ? { label: 'All refills', to: '/refills' } : undefined}
      />

      <InventoryListFiltersCard>
        <Select
          value={filter}
          onValueChange={(value) => {
            if (isRefillListFilter(value)) updateParams({ filter: value });
          }}
        >
          <SelectTrigger className="w-full sm:w-[220px]" aria-label="Filter refills">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            {REFILL_LIST_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </InventoryListFiltersCard>

      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </div>
      ) : null}

      <InventoryDataTableCard
        loading={loading}
        isEmpty={!error && items.length === 0}
        colSpan={6}
        emptyMessage={emptyRefillListMessage(filter, Boolean(machineId))}
        header={
          <>
            <InventoryTableHead>Machine</InventoryTableHead>
            <InventoryTableHead className="hidden md:table-cell">Venue</InventoryTableHead>
            <InventoryTableHead>Status</InventoryTableHead>
            <InventoryTableHead className="hidden sm:table-cell">Source</InventoryTableHead>
            <InventoryTableHead className="text-right">Leftover</InventoryTableHead>
            <InventoryTableHead className="hidden sm:table-cell">When</InventoryTableHead>
          </>
        }
        footer={
          total > 0 ? (
            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={total}
              itemLabel="refills"
              pageSize={PAGE_SIZE}
              onPageChange={(nextPage) => updateParams({ page: nextPage })}
            />
          ) : undefined
        }
      >
        {items.map((item) => (
          <TableRow key={item.id} className={item.leftoverPending ? 'bg-amber-50' : undefined}>
            <TableCell>
              <div className="flex min-w-0 flex-col">
                <MachineLink machineId={item.machineId} name={item.machineName} />
                <TableLink to={refillSessionPath(item.id)} className="text-xs text-muted-foreground">
                  Open session
                </TableLink>
              </div>
            </TableCell>
            <TableCell className="hidden text-muted-foreground md:table-cell">
              {item.venueName || '—'}
            </TableCell>
            <TableCell>
              <RefillStatusBadge status={item.status} />
            </TableCell>
            <TableCell className="hidden sm:table-cell">
              <RefillSourceBadge source={item.completionSource} />
            </TableCell>
            <TableCell className={`text-right ${item.leftoverPending ? 'font-semibold text-amber-800' : ''}`}>
              {item.leftoverPending
                ? item.leftoverUnits
                : item.leftoversReturned && item.leftoverUnits > 0
                  ? `${item.leftoverUnits} returned`
                  : '—'}
            </TableCell>
            <TableCell className="hidden text-muted-foreground sm:table-cell">
              {formatRefillWhen(item)}
            </TableCell>
          </TableRow>
        ))}
      </InventoryDataTableCard>
    </div>
  );
}

export default RefillsList;
